import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';

import './MemoryMap.css';

const COL_X = [0, 240, 520, 820];
const ROW_H = 70;
const TOP_PAD = 40;

const PALETTE = {
  root: {
    background: '#229adb',
    border: '2px solid #1a7eb6',
    color: 'white',
    fontWeight: 700,
    fontSize: 16,
    width: 200,
    cursor: 'default',
  },
  section: {
    background: '#eaf6fc',
    border: '2px solid #229adb',
    color: '#155a85',
    fontWeight: 700,
    fontSize: 14,
    width: 230,
    cursor: 'pointer',
  },
  point: {
    background: '#fff8e1',
    border: '2px solid #f1c84b',
    color: '#6a4b00',
    fontWeight: 600,
    fontSize: 13,
    width: 250,
    cursor: 'default',
  },
  subpoint: {
    background: '#ffffff',
    border: '1px solid #cbd5e1',
    color: '#4b5563',
    fontWeight: 500,
    fontSize: 12,
    width: 200,
    cursor: 'default',
  },
};

const nodeStyle = (kind) => ({
  ...PALETTE[kind],
  padding: '10px 14px',
  borderRadius: 12,
  textAlign: 'center',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  lineHeight: 1.3,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
});

const buildLayout = (rootLabel, sections) => {
  const nodes = [];
  const edges = [];

  const sectionLeafCounts = sections.map((s) =>
    s.descriptionPoints.reduce(
      (acc, p) => acc + Math.max(1, (p.subpoints || []).length),
      0
    )
  );
  const totalLeaves = sectionLeafCounts.reduce((a, b) => a + b, 0);
  const totalHeight = Math.max(totalLeaves, 1) * ROW_H;

  nodes.push({
    id: 'root',
    position: { x: COL_X[0], y: TOP_PAD + totalHeight / 2 - 25 },
    data: { label: rootLabel, kind: 'root' },
    style: nodeStyle('root'),
    sourcePosition: 'right',
    targetPosition: 'left',
    draggable: false,
  });

  let sectionCursorY = TOP_PAD;

  sections.forEach((section, sIdx) => {
    const leaves = Math.max(sectionLeafCounts[sIdx], 1);
    const sectionId = `s-${sIdx}`;
    const sectionY = sectionCursorY + (leaves * ROW_H) / 2 - 22;

    nodes.push({
      id: sectionId,
      position: { x: COL_X[1], y: sectionY },
      data: { label: section.title, kind: 'section', link: section.link },
      style: nodeStyle('section'),
      sourcePosition: 'right',
      targetPosition: 'left',
      draggable: false,
    });

    edges.push({
      id: `e-root-${sectionId}`,
      source: 'root',
      target: sectionId,
      type: 'smoothstep',
      style: { stroke: '#229adb', strokeWidth: 2 },
    });

    let pointCursorY = sectionCursorY;

    section.descriptionPoints.forEach((point, pIdx) => {
      const subs = point.subpoints || [];
      const pointLeaves = Math.max(1, subs.length);
      const pointId = `${sectionId}-p-${pIdx}`;
      const pointY = pointCursorY + (pointLeaves * ROW_H) / 2 - 18;

      nodes.push({
        id: pointId,
        position: { x: COL_X[2], y: pointY },
        data: { label: point.text, kind: 'point' },
        style: nodeStyle('point'),
        sourcePosition: 'right',
        targetPosition: 'left',
        draggable: false,
      });

      edges.push({
        id: `e-${sectionId}-${pointId}`,
        source: sectionId,
        target: pointId,
        type: 'smoothstep',
        style: { stroke: '#d68f00', strokeWidth: 1.5 },
      });

      subs.forEach((sub, kIdx) => {
        const subId = `${pointId}-sp-${kIdx}`;
        const subY = pointCursorY + kIdx * ROW_H;

        nodes.push({
          id: subId,
          position: { x: COL_X[3], y: subY },
          data: { label: sub, kind: 'subpoint' },
          style: nodeStyle('subpoint'),
          sourcePosition: 'right',
          targetPosition: 'left',
          draggable: false,
        });

        edges.push({
          id: `e-${pointId}-${subId}`,
          source: pointId,
          target: subId,
          type: 'smoothstep',
          style: { stroke: '#cbd5e1', strokeWidth: 1 },
        });
      });

      pointCursorY += pointLeaves * ROW_H;
    });

    sectionCursorY += leaves * ROW_H;
  });

  return { nodes, edges };
};

const miniMapNodeColor = (node) => {
  const kind = node.data?.kind;
  if (kind === 'root') return '#229adb';
  if (kind === 'section') return '#7fc4e8';
  if (kind === 'point') return '#f1c84b';
  return '#cbd5e1';
};

const MemoryMapFlow = ({ title, sections }) => {
  const navigate = useNavigate();
  const { nodes, edges } = useMemo(
    () => buildLayout(title, sections),
    [title, sections]
  );

  const onNodeClick = (_, node) => {
    if (node.data?.kind === 'section' && node.data.link) {
      navigate(node.data.link);
    }
  };

  return (
    <div className="memory-map">
      <h1 className="main-title">{title}</h1>
      <p className="memory-map-hint">
        Click a <strong>blue topic</strong> to start practicing.
        Drag the canvas to pan and scroll to zoom.
      </p>

      <div className="memory-map-canvas">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodeClick={onNodeClick}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.3}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            panOnScroll
            zoomOnScroll
          >
            <Background gap={20} color="#e0e7ef" />
            <Controls showInteractive={false} />
            <MiniMap
              zoomable
              pannable
              nodeColor={miniMapNodeColor}
              maskColor="rgba(34, 154, 219, 0.08)"
            />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
};

export default MemoryMapFlow;
